import { useState, useEffect, useRef } from 'react';
import { variablesTema, cargarFuente } from '../tema';
import { useParams } from 'react-router-dom';
import TicketQR from '../components/TicketQR';
import PedirServicio from './PedirServicio';
import { fmtDate, money } from '../events/util';

const API = process.env.REACT_APP_API_URL || '';

// Link del comprador de una mesa, protegido por un PIN de 4 dígitos que crea el comprador
// (así el RRPP tiene el link pero no ve las entradas ni el código de consumo). El saldo se
// muestra en tiempo real (poll cada 20s). El PIN queda desbloqueado en ese dispositivo (token).
export default function MesaLink() {
  const { code } = useParams();
  const CODE = (code || '').toUpperCase();
  const tokenKey = `tk_mesa_${CODE}`;
  const [m, setM] = useState(null);
  const [error, setError] = useState('');
  const [locked, setLocked] = useState(true);
  const [hasPin, setHasPin] = useState(false);
  const [pin, setPin] = useState('');
  const [responsable, setResponsable] = useState('');
  const [pinErr, setPinErr] = useState('');
  const [busy, setBusy] = useState(false);
  const token = useRef(null);
  const timer = useRef(null);

  const startPoll = () => {
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      fetch(`${API}/api/public/mesa/${CODE}?token=${encodeURIComponent(token.current)}`)
        .then(r => r.json()).then(d => { if (d.unlocked) setM(d); }).catch(() => {});
    }, 20000);
  };

  useEffect(() => {
    try { token.current = localStorage.getItem(tokenKey); } catch { token.current = null; }
    const url = token.current ? `${API}/api/public/mesa/${CODE}?token=${encodeURIComponent(token.current)}` : `${API}/api/public/mesa/${CODE}`;
    fetch(url).then(r => r.ok ? r.json() : Promise.reject(r)).then(d => {
      setM(d);
      cargarFuente(d && d.tipografia);
      if (d.unlocked) { setLocked(false); startPoll(); }
      else { setLocked(true); setHasPin(!!d.has_pin); }
    }).catch(() => setError('Mesa no encontrada.'));
    return () => clearInterval(timer.current);
  }, [CODE]); // eslint-disable-line

  async function unlock() {
    if (!/^\d{4}$/.test(pin)) { setPinErr('El PIN son 4 dígitos'); return; }
    setBusy(true); setPinErr('');
    try {
      const r = await fetch(`${API}/api/public/mesa/${CODE}/unlock`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ pin, responsable: responsable.trim() || undefined }) });
      const d = await r.json();
      if (!r.ok) { setPinErr((d.error || 'Error') + (d.attempts_left != null ? ` · ${d.attempts_left} intento${d.attempts_left === 1 ? '' : 's'}` : '')); return; }
      token.current = d.token;
      try { localStorage.setItem(tokenKey, d.token); } catch {}
      setM(d); setLocked(false); setPin(''); startPoll();
    } catch { setPinErr('Error de conexión'); } finally { setBusy(false); }
  }

  // Generar la entrada de un asistente (con sus datos)
  const EMPTY_GF = { buyer_name: '', buyer_lastname: '', buyer_email: '', buyer_dni: '', buyer_phone: '', buyer_city: '', buyer_birthdate: '', marketing_consent: false };
  const [genOpen, setGenOpen] = useState(false);
  const [gf, setGf] = useState(EMPTY_GF);
  const [genErr, setGenErr] = useState('');
  const [genBusy, setGenBusy] = useState(false);
  const setG = (k, v) => setGf(s => ({ ...s, [k]: v }));
  const reload = () => fetch(`${API}/api/public/mesa/${CODE}?token=${encodeURIComponent(token.current)}`).then(r => r.json()).then(d => { if (d.unlocked) setM(d); }).catch(() => {});
  async function generate() {
    if (!gf.buyer_name.trim() || !gf.buyer_lastname.trim() || !gf.buyer_email.trim()) { setGenErr('Nombre, apellido y email son obligatorios'); return; }
    setGenBusy(true); setGenErr('');
    try {
      const r = await fetch(`${API}/api/public/mesa/${CODE}/generate-qr`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: token.current, buyer: gf }) });
      const d = await r.json();
      if (!r.ok) { setGenErr(d.error || 'No se pudo generar'); return; }
      setGf(EMPTY_GF); setGenOpen(false); await reload();
    } catch { setGenErr('Error de conexión'); } finally { setGenBusy(false); }
  }

  const qrs = Array.isArray(m?.qr_codes) ? m.qr_codes : [];
  const remaining = m ? Math.max(0, Number(m.capacity || 0) - qrs.length) : 0;

  return (
    <div style={{ ...variablesTema(m), minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 20px' }}>
      {m?.logo_url
        ? <img src={m.logo_url} alt={m.org_name || ''} style={{ height: 42, marginBottom: 24, objectFit: 'contain' }} />
        : <div style={{ marginBottom: 24, fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 20, color: '#fff', textTransform: 'uppercase' }}>{m?.org_name || ''}</div>}
      {error && <div style={{ color: 'var(--red-300)' }}>{error}</div>}
      {!error && !m && <div style={{ color: 'var(--ink-600)' }}>Cargando…</div>}
      {m && (
        <div style={{ width: '100%', maxWidth: 460, textAlign: 'center' }}>
          <div className="lb-display" style={{ fontSize: 24 }}>{m.event_name}</div>
          <div style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 4, textTransform: 'capitalize' }}>{fmtDate(m.date)}</div>
          <div style={{ marginTop: 10, fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 20, color: '#fff', textTransform: 'uppercase' }}>
            {m.sector_name ? `${m.sector_name} · ` : ''}{m.mesa_name}
          </div>
          <div style={{ color: 'var(--ink-700)', fontSize: 13, marginTop: 2 }}>{m.capacity} personas</div>

          {locked ? (
            <div className="lb-card" style={{ padding: '26px 24px', marginTop: 24, textAlign: 'left' }}>
              <div className="lb-h" style={{ fontSize: 18, marginBottom: 8 }}>{hasPin ? 'Ingresá tu PIN' : 'Creá tu PIN'}</div>
              <p style={{ fontSize: 13.5, color: 'var(--ink-700)', lineHeight: 1.55, marginTop: 0 }}>
                {hasPin
                  ? 'Ingresá el PIN de 4 dígitos que creaste para ver tus entradas y tu saldo de consumo.'
                  : 'Creá un PIN de 4 dígitos y recordalo. Lo vas a necesitar para ver tus entradas, tu código de consumo y tu saldo de consumo durante toda la noche.'}
              </p>
              {!hasPin && (
                <div style={{ marginBottom: 12 }}>
                  <div className="lb-label" style={{ marginBottom: 6 }}>¿A nombre de quién está la mesa?</div>
                  <input className="lb-input" value={responsable} onChange={e => setResponsable(e.target.value)}
                    placeholder="Nombre del responsable" style={{ textAlign: 'center' }} />
                  <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 6 }}>Con este nombre el boliche identifica tu mesa en el salón.</div>
                </div>
              )}
              <input className="lb-input" inputMode="numeric" maxLength={4} value={pin} autoFocus={hasPin}
                onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                onKeyDown={e => e.key === 'Enter' && unlock()}
                placeholder="● ● ● ●" style={{ fontSize: 26, textAlign: 'center', letterSpacing: '0.5em', marginTop: 4 }} />
              {pinErr && <div className="lb-err" style={{ marginTop: 8 }}>{pinErr}</div>}
              <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: 14 }} disabled={busy || pin.length !== 4} onClick={unlock}>
                {busy ? '…' : (hasPin ? 'Desbloquear →' : 'Crear PIN y ver mis entradas →')}
              </button>
            </div>
          ) : (
            <>
              {m.consumo_code && m.consumo_total > 0 && (() => {
                const upd = m.consumo_updated_at ? new Date(m.consumo_updated_at) : null;
                const updStr = upd && !isNaN(upd) ? upd.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Argentina/Buenos_Aires' }) : null;
                return (
                <div style={{ marginTop: 18, padding: '16px 18px', border: '1px solid rgba(236,38,143,0.4)', background: 'rgba(236,38,143,0.06)', borderRadius: 8 }}>
                  <div style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#FF9DCD' }}>Tu saldo de consumo</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 30, color: '#fff', marginTop: 6 }}>{money(m.consumo_remaining != null ? m.consumo_remaining : m.consumo_total)}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 2 }}>de {money(m.consumo_total)} de consumo{updStr ? ` · actualizado a las ${updStr}` : ''}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 20, letterSpacing: '0.12em', color: '#FF9DCD', marginTop: 10 }}>{m.consumo_code}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 8, lineHeight: 1.5 }}>Si acabás de consumir en la barra, puede tardar unos minutos en reflejarse acá (el saldo se actualiza cuando la barra sincroniza). Ingresá este código en la barra para usarlo — es tuyo, no lo compartas.</div>
                </div>
                );
              })()}

              {/* Servicio a la mesa. Va JUSTO DEBAJO del saldo a propósito: es la acción
                  que más se va a usar durante la noche, y el saldo es lo que define
                  cuánto va a tener que pagar aparte. */}
              {/* token.current, no el ref: adentro se usa como valor para las llamadas. */}
              <PedirServicio code={CODE} token={token.current} mesa={m} onSaldo={reload} />

              <div className="lb-eyebrow" style={{ marginTop: 24, marginBottom: 8, textAlign: 'left' }}>Entradas · {qrs.length} de {m.capacity} generadas</div>

              {/* entradas ya generadas */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                {qrs.map((q, i) => (
                  <div key={q}>
                    <div style={{ fontSize: 12, color: 'var(--ink-700)', marginBottom: 8 }}>Entrada {i + 1}</div>
                    <TicketQR value={q} logo={m.logo_url} eventName={m.event_name} date={m.date} lotName={`${m.sector_name || ''} ${m.mesa_name}`.trim()} attendeeName={''} filename={`${m.event_name}-${q}.png`} />
                  </div>
                ))}
              </div>

              {/* cupos por generar */}
              {remaining > 0 && !genOpen && (
                <button className="lb-btn lb-btn--primary lb-btn--block" style={{ marginTop: qrs.length ? 24 : 8 }} onClick={() => { setGenErr(''); setGenOpen(true); }}>
                  + Generar mi entrada <span style={{ opacity: 0.7 }}>({remaining} disponible{remaining === 1 ? '' : 's'})</span>
                </button>
              )}
              {remaining > 0 && genOpen && (
                <div className="lb-card" style={{ padding: '22px 22px', marginTop: 20, textAlign: 'left' }}>
                  <div className="lb-h" style={{ fontSize: 17, marginBottom: 4 }}>Tus datos</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 14 }}>Cargá tus datos para generar tu entrada. Con tu email la vas a ver también en tu cuenta.</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <F label="Nombre"><input className="lb-input" value={gf.buyer_name} onChange={e => setG('buyer_name', e.target.value)} /></F>
                    <F label="Apellido"><input className="lb-input" value={gf.buyer_lastname} onChange={e => setG('buyer_lastname', e.target.value)} /></F>
                  </div>
                  <div style={{ marginTop: 10 }}><F label="Email"><input className="lb-input" type="email" value={gf.buyer_email} onChange={e => setG('buyer_email', e.target.value)} /></F></div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                    <F label="DNI"><input className="lb-input" inputMode="numeric" value={gf.buyer_dni} onChange={e => setG('buyer_dni', e.target.value.replace(/\D/g, ''))} /></F>
                    <F label="Teléfono"><input className="lb-input" inputMode="tel" value={gf.buyer_phone} onChange={e => setG('buyer_phone', e.target.value)} /></F>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                    <F label="Localidad"><input className="lb-input" value={gf.buyer_city} onChange={e => setG('buyer_city', e.target.value)} /></F>
                    <F label="Fecha de nacimiento"><input className="lb-input" type="date" value={gf.buyer_birthdate} onChange={e => setG('buyer_birthdate', e.target.value)} /></F>
                  </div>
                  <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: 'var(--ink-700)', lineHeight: 1.45, cursor: 'pointer', marginTop: 14 }}>
                    <input type="checkbox" checked={gf.marketing_consent} onChange={e => setG('marketing_consent', e.target.checked)} style={{ marginTop: 2 }} />
                    <span>Quiero recibir novedades y promociones de <b style={{ color: '#fff' }}>{m?.org_name || 'este boliche'}</b> por email. <span style={{ color: 'var(--ink-600)' }}>(Podés darte de baja cuando quieras.)</span></span>
                  </label>
                  {genErr && <div className="lb-err" style={{ marginTop: 8 }}>{genErr}</div>}
                  <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                    <button className="lb-btn lb-btn--ghost" style={{ flex: 1 }} onClick={() => setGenOpen(false)}>Cancelar</button>
                    <button className="lb-btn lb-btn--primary" style={{ flex: 2 }} disabled={genBusy} onClick={generate}>{genBusy ? 'Generando…' : 'Generar mi entrada →'}</button>
                  </div>
                </div>
              )}
              {remaining === 0 && qrs.length > 0 && <div style={{ marginTop: 16, fontSize: 12, color: 'var(--ink-600)' }}>Todas las entradas de la mesa están generadas. Mostrá cada QR en la puerta.</div>}

              <div style={{ textAlign: 'center', marginTop: 28, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-600)' }}>
                Acceso reservado · Dress code estricto · +18
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function F({ label, children }) {
  return <div><div style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-600)', marginBottom: 5 }}>{label}</div>{children}</div>;
}
